import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ScoreTimeline from './components/ScoreTimeline';


const UserProfile = () => {
    const { username } = useParams();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sendingRequest, setSendingRequest] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, [username]);

    const fetchProfile = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get(`/api/users/profile/${username}`);
            setProfile(res.data);
        } catch (err) {
            console.error('Error fetching profile:', err);
            if (err.response?.status === 403) {
                setError('This profile is private');
            } else if (err.response?.status === 404) {
                setError('User not found');
            } else {
                setError('Failed to load profile');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAddFriend = async () => {
        if (!profile || sendingRequest) return;
        setSendingRequest(true);
        try {
            await api.post('/api/friends/request', { friend_id: profile.id });
            // Update local state to show pending
            setProfile(prev => ({
                ...prev,
                friendship: { ...prev.friendship, status: 'pending' }
            }));
        } catch (err) {
            console.error('Error sending friend request:', err);
        } finally {
            setSendingRequest(false);
        }
    };

    const handleUnfriend = async () => {
        if (!profile || sendingRequest) return;
        if (!window.confirm(`Are you sure you want to unfriend ${profile.full_name}?`)) return;
        
        setSendingRequest(true);
        try {
            await api.post('/api/friends/remove', { friend_id: profile.id });
            setProfile(prev => ({
                ...prev,
                friendship: { ...prev.friendship, status: 'none' }
            }));
        } catch (err) {
            console.error('Error removing friend:', err);
        } finally {
            setSendingRequest(false);
        }
    };

    // Calculate Heatmap Data
    const heatmapData = useMemo(() => {
        const activityDates = profile?.activity_dates || [];
        
        // Safely extract and format activity dates
        const dates = activityDates
            .map(d => {
                const date = new Date(d.active_date);
                return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
            })
            .filter(Boolean);

        const data = [];
        const today = new Date();
        for (let i = 0; i < 365; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() - i);
            const dateStr = date.toISOString().split('T')[0];
            data.unshift({
                date: dateStr,
                count: dates.filter(d => d === dateStr).length,
                day: date.getDay()
            });
        }
        return data;
    }, [profile]);


    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center pt-24">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                        <div className="w-6 h-6 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                    <p className="text-sm font-bold text-slate-500">Loading profile...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#F8FAFC]">
                <NavigationHeader />
                <main className="pt-32 pb-12 px-6">
                    <div className="max-w-2xl mx-auto text-center">
                        <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6 text-slate-400">
                            <Icon name="Lock" size={32} />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 mb-2">{error}</h1>
                        <p className="text-slate-500 font-bold mb-8text-[10px] tracking-widest">Access Restricted</p>
                        <Button onClick={() => navigate('/leaderboard')} className="bg-slate-900 hover:bg-emerald-500 h-11 px-8 rounded-xl font-blacktext-xs tracking-widest text-white transition-all shadow-lg shadow-slate-900/10">
                            Back to Leaderboard
                        </Button>
                    </div>
                </main>
            </div>
        );
    }

    const { friendship, stats, strengths, activity } = profile;

    const getRankInfo = (score) => {
        if (score >= 95) return { name: 'Grandmaster', color: 'text-red-500', bg: 'bg-red-50' };
        if (score >= 85) return { name: 'Master', color: 'text-purple-500', bg: 'bg-purple-50' };
        if (score >= 70) return { name: 'Expert', color: 'text-blue-500', bg: 'bg-blue-50' };
        if (score >= 50) return { name: 'Specialist', color: 'text-cyan-500', bg: 'bg-cyan-50' };
        if (score >= 30) return { name: 'Apprentice', color: 'text-emerald-500', bg: 'bg-emerald-50' };
        return { name: 'Newbie', color: 'text-slate-400', bg: 'bg-slate-50' };
    };

    const rank = getRankInfo(profile.performance_score || 0);

    return (
        <div className="min-h-screen bg-white relative overflow-hidden">
            {/* Premium Header Background */}
            <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
            <NavigationHeader />
            
            <main className="max-w-5xl mx-auto px-6 pt-24 pb-12 relative z-10">
                <AnimatePresence mode="wait">
                    <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-6"
                    >
                        {/* CODEFORCES STYLE HEADER */}
                        <div className="bg-white rounded-[24px] p-6 border border-slate-100 shadow-sm relative overflow-hidden">
                            <div className="flex flex-col md:flex-row gap-8">
                                {/* Left Info Block */}
                                <div className="flex-1 space-y-6">
                                    <div>
                                        <div className="flex items-center gap-3 mb-1">
                                            <span className={`text-xs font-blacktracking-widest ${rank.color}`}>
                                                {rank.name}
                                            </span>
                                        </div>
                                        <h1 className={`text-xl font-bold tracking-tight mb-1 ${rank.color}`}>
                                            {profile.full_name}
                                        </h1>

                                        <div className="flex items-center gap-2 text-slate-400 font-bold text-xstracking-widest">
                                            <Icon name="AtSign" size={14} />
                                            {profile.username}
                                        </div>
                                    </div>

                                    <div className="space-y-4 pt-2">
                                        <div className="flex items-center gap-3 text-slate-600">
                                            <Icon name="GraduationCap" size={18} className="text-slate-400" />
                                            <span className="text-sm font-boldtracking-wide">
                                                From <span className="text-slate-900">{profile.college || 'IIT Delhi'}</span>
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 text-slate-600">
                                            <Icon name="Briefcase" size={18} className="text-slate-400" />
                                            <span className="text-sm font-boldtracking-wide">
                                                Targeting <span className="text-slate-900">{profile.target_role || 'SDE-II'}</span>
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 text-slate-600">
                                            <Icon name="Calendar" size={18} className="text-slate-400" />
                                            <span className="text-sm font-boldtracking-wide">
                                                Joined <span className="text-slate-900">
                                                    {(() => {
                                                        const date = new Date(profile.created_at);
                                                        return isNaN(date.getTime()) 
                                                            ? 'Recently' 
                                                            : date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
                                                    })()}
                                                </span>
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-8 py-4 border-y border-slate-50">
                                        <div>
                                            <div className="text-[10px] font-black text-slate-400tracking-widest mb-1">Current Rating</div>
                                            <div className="flex items-baseline gap-2">
                                                <span className={`text-lg font-bold ${rank.color}`}>{profile.performance_score || 0}</span>
                                                <span className="text-[10px] font-bold text-slate-300tracking-widest">Score</span>

                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] font-black text-slate-400tracking-widest mb-1">Global Standing</div>
                                            <div className="flex items-baseline gap-2">
                                                <span className="text-lg font-bold text-slate-900">#{profile.rank || '-'}</span>

                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3 pt-2">
                                        {!friendship.is_self && (
                                            <>
                                                {friendship.status === 'none' && (
                                                    <Button 
                                                        onClick={handleAddFriend}
                                                        isLoading={sendingRequest}
                                                        className="bg-emerald-500 hover:bg-emerald-600 text-white h-11 px-8 rounded-xl font-blacktext-[11px] tracking-widest flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition-all active:scale-95"
                                                    >
                                                        <Icon name="UserPlus" size={16} />
                                                        Add Friend
                                                    </Button>
                                                )}
                                                {friendship.status === 'pending' && (
                                                    <div className="bg-slate-100 text-slate-400 h-11 px-8 rounded-xl font-blacktext-[11px] tracking-widest flex items-center gap-2 border border-slate-200 cursor-default">
                                                        <Icon name="Clock" size={16} />
                                                        Pending
                                                    </div>
                                                )}
                                                {friendship.status === 'accepted' && (
                                                    <Button 
                                                        onClick={handleUnfriend}
                                                        isLoading={sendingRequest}
                                                        className="bg-white hover:bg-red-50 text-slate-400 hover:text-red-500 h-11 px-8 rounded-xl font-blacktext-[11px] tracking-widest flex items-center gap-2 border border-slate-200 hover:border-red-100 transition-all active:scale-95"
                                                    >
                                                        <Icon name="UserCheck" size={16} />
                                                        Unfriend
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                        <div className="flex items-center gap-6 ml-4">
                                            <div className="text-center">
                                                <div className="text-[10px] font-black text-slate-300tracking-widest">Sessions</div>
                                                <div className="text-sm font-black text-slate-900">{stats?.total_interviews || 0}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Profile Photo */}
                                <div className="shrink-0">
                                    <div className="relative group">
                                        <div className="w-40 h-40 rounded-2xl border-4 border-white shadow-xl overflow-hidden bg-slate-50 ring-1 ring-slate-100 transition-transform group-hover:scale-[1.02] duration-500">

                                            <img 
                                                src={profile.profile_picture_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${profile.full_name}`} 
                                                alt="" 
                                                className="w-full h-full object-cover" 
                                            />
                                        </div>
                                        <div className={`absolute -top-3 -right-3 px-4 py-2 ${rank.bg} ${rank.color} rounded-2xl border-4 border-white font-black text-[10px]tracking-widest shadow-xl`}>
                                            {rank.name}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* STATS & HEATMAP SECTION */}
                        <div className="space-y-6">
                            {/* HEATMAP */}
                            <div className="bg-white p-6 rounded-[24px] border border-slate-100 shadow-sm">
                                    <div className="flex items-center justify-between mb-8">
                                        <h3 className="text-sm font-black text-slate-900tracking-widest flex items-center gap-2">
                                            <Icon name="Calendar" size={18} className="text-emerald-500" />
                                            Practice Consistency
                                        </h3>
                                        <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400">
                                            <span>Less</span>
                                            <div className="flex gap-1">
                                                {[1, 2, 3, 4, 5].map(i => (
                                                    <div key={i} className={`w-2.5 h-2.5 rounded-sm bg-emerald-500 opacity-${i * 20}`} />
                                                ))}
                                            </div>
                                            <span>More</span>
                                        </div>
                                    </div>
                                    
                                    <div className="overflow-x-auto pb-4 custom-scrollbar">
                                        <div className="flex flex-col gap-1 min-w-[800px]">
                                            <div className="grid grid-flow-col auto-cols-max gap-1">
                                                {/* Rows for Mon, Wed, Fri labels */}
                                                <div className="flex flex-col gap-3 pr-2 text-[10px] font-bold text-slate-300py-2">
                                                    <span>M</span>
                                                    <span>W</span>
                                                    <span>F</span>
                                                </div>
                                                
                                                {/* Squares */}
                                                <div className="flex gap-1">
                                                    {Array.from({ length: 52 }).map((_, weekIdx) => (
                                                        <div key={weekIdx} className="flex flex-col gap-1">
                                                            {Array.from({ length: 7 }).map((_, dayIdx) => {
                                                                const dayData = heatmapData[weekIdx * 7 + dayIdx];
                                                                const level = dayData?.count ? (dayData.count > 3 ? 4 : dayData.count) : 0;
                                                                return (
                                                                    <div 
                                                                        key={dayIdx} 
                                                                        className={`w-3 h-3 rounded-sm transition-all hover:scale-125 hover:shadow-lg ${
                                                                            level === 0 ? 'bg-slate-50' : 
                                                                            level === 1 ? 'bg-emerald-100' :
                                                                            level === 2 ? 'bg-emerald-300' :
                                                                            level === 3 ? 'bg-emerald-500' : 'bg-emerald-600'
                                                                        } cursor-pointer`}
                                                                        title={dayData?.date || ''}
                                                                    />
                                                                );
                                                            })}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-50">
                                        <div className="text-[11px] font-bold text-slate-400tracking-widest">
                                            Total activity: <span className="text-slate-900 font-black">{stats?.total_interviews || 482} sessions</span> last year
                                        </div>
                                        <div className="flex items-center gap-1.5 text-[11px] font-black text-slate-900tracking-widest">
                                            18 day streak
                                            <Icon name="Flame" size={14} className="text-orange-500" />
                                        </div>
                                    </div>
                                </div>

                                {/* RATING TIMELINE */}
                                <div className="bg-white p-6 rounded-[24px] border border-slate-100 shadow-sm">
                                    <h3 className="text-sm font-black text-slate-900tracking-widest mb-2 flex items-center gap-2">
                                        <Icon name="TrendingUp" size={18} className="text-emerald-500" />
                                        Rating History
                                    </h3>
                                    <ScoreTimeline activity={activity} />
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </main>
            </div>

    );
};


const ActivityItem = ({ title, subtitle, status }) => (
    <div className="flex items-center gap-4 group cursor-pointer">
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.3)] shrink-0 transition-transform group-hover:scale-125" />
        <div className="flex-1">
            <h4 className="text-[11px] font-black text-slate-800 leading-tight group-hover:text-emerald-600 transition-colors">{title}</h4>
            <p className="text-[9px] font-bold text-slate-400 mt-1tracking-widest">{subtitle}</p>
        </div>
        <div className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[8px] font-blackrounded tracking-wider border border-emerald-100">
            {status}
        </div>
    </div>
);

export default UserProfile;
